import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { BadgeCheck, Banknote, Lock, Trash2, Undo2 } from "lucide-react";
import { api } from "@/lib/api";
import { WhatsAppButton, WhatsAppHistory } from "@/components/WhatsApp";
import { useAuth } from "@/lib/auth";
import { act, useFetch } from "@/lib/hooks";
import { can, da, fmtDate, PAY_METHODS } from "@/lib/format";
import { Empty, Field, Loading, PageHeader, Panel, PdfButton, StatusBadge } from "@/components/common";
import { LinesEditor, LinesView, Totals } from "@/components/LinesEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const stampDuty = (amount: number) => {
  const rate = amount <= 300 ? 0 : amount <= 30000 ? 1 : amount <= 100000 ? 1.5 : 2;
  return { rate, stamp: Math.round(amount * rate) / 100 };
};

function PaymentForm({ inv, onDone }: { inv: any; onDone: () => void }) {
  const [amount, setAmount] = useState<any>(inv.balance);
  const [method, setMethod] = useState(inv.payment_terms || "especes");
  const [ref, setRef] = useState("");
  useEffect(() => setAmount(inv.balance), [inv.balance]);
  const pay = async () => {
    if (await act(() => api.post(`/invoices/${inv.id}/payments`, { amount: Number(amount), method, reference: ref }), inv.client_kind === "entreprise" ? "Encaissement enregistré, reçu émis" : "Encaissement enregistré, bon de paiement émis")) onDone();
  };
  return (
    <div className="space-y-3" data-testid="payment-form">
      <Field label="Montant (DA)"><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-12 font-mono-data text-lg" data-testid="payment-amount-input" /></Field>
      <div className="grid grid-cols-2 gap-2">
        {Object.entries(PAY_METHODS).map(([k, l]) => (
          <button key={k} onClick={() => setMethod(k)} className={`h-11 rounded-sm border text-sm transition-colors ${method === k ? "border-primary bg-primary/10" : "border-border hover:bg-accent"}`} data-testid={`payment-method-${k}`}>{l}</button>
        ))}
      </div>
      {method !== "especes" && <Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="N° chèque / référence" data-testid="payment-ref-input" />}
      <Button className="h-12 w-full" onClick={pay} data-testid="payment-submit-btn"><Banknote className="mr-2 h-4 w-4" />Encaisser</Button>
    </div>
  );
}

function CreditDialog({ inv, open, onOpenChange, onDone }: any) {
  const [reason, setReason] = useState("");
  const [lines, setLines] = useState<any[]>([]);
  const [partial, setPartial] = useState(false);
  useEffect(() => {
    if (open) setLines(inv.lines.map((l: any) => ({ ...l })));
  }, [open, inv.lines]);
  const save = async () => {
    const r = await act(() => api.post(`/invoices/${inv.id}/credit-note`, { reason, lines: partial ? lines : null }), "Avoir émis");
    if (r) onDone(r.data.id);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl" data-testid="credit-dialog">
        <DialogHeader><DialogTitle className="font-display uppercase">Établir un avoir sur {inv.number}</DialogTitle></DialogHeader>
        <Field label="Motif"><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Erreur de prix, geste commercial…" data-testid="credit-reason-input" /></Field>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={partial} onChange={(e) => setPartial(e.target.checked)} data-testid="credit-partial-checkbox" />Avoir partiel (modifier les lignes à créditer)</label>
        {partial ? <LinesEditor lines={lines} onChange={setLines} defaultTva={0} /> : <p className="text-sm text-muted-foreground">Avoir total : {da(inv.totals.ttc - (inv.credited || 0))}</p>}
        <Button className="h-11" disabled={reason.length < 3} onClick={save} data-testid="credit-save-btn"><Undo2 className="mr-2 h-4 w-4" />Émettre l'avoir</Button>
      </DialogContent>
    </Dialog>
  );
}

function RefundPanel({ inv, canPay, onDone }: { inv: any; canPay: boolean; onDone: () => void }) {
  const [method, setMethod] = useState("especes");
  const left = inv.refund_due - (inv.refunded || 0);
  const refund = async () => {
    if (await act(() => api.post(`/invoices/${inv.id}/refund`, { method }), "Remboursement enregistré")) onDone();
  };
  return (
    <Panel title="Remboursement client" testId="refund-panel">
      <p className="text-sm">Cet avoir porte sur un montant déjà encaissé : <b className="font-mono-data">{da(inv.refund_due)}</b> à rendre au client.</p>
      {left > 0.009 && canPay ? (
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(PAY_METHODS).map(([k, l]) => (
            <button key={k} onClick={() => setMethod(k)} className={`h-10 rounded-sm border px-3 text-sm ${method === k ? "border-primary bg-primary/10" : "border-border"}`} data-testid={`refund-method-${k}`}>{l}</button>
          ))}
          <Button onClick={refund} data-testid="refund-submit-btn">Rembourser {da(left)}</Button>
        </div>
      ) : left <= 0.009 && (
        <div className="mt-3 space-y-2" data-testid="refund-done">
          <p className="text-sm text-emerald-400">Remboursement effectué.</p>
          {inv.payments.map((p: any) => <PdfButton key={p.id} kind="receipt" id={p.id} label={`Bon ${p.number}`} testId={`refund-pdf-${p.id}`} />)}
        </div>
      )}
    </Panel>
  );
}

function StampLines({ rate, stamp, net }: { rate: number; stamp: number; net: number }) {
  return (
    <div className="ml-auto mt-2 w-full max-w-xs space-y-1 text-sm" data-testid="invoice-stamp-block">
      <div className="flex justify-between"><span>Timbre fiscal ({String(rate).replace(".", ",")} %)</span><span className="font-mono-data" data-testid="invoice-stamp-amount">{da(stamp)}</span></div>
      <div className="flex justify-between border-t border-border pt-1 text-base font-bold"><span>Net à payer</span><span className="font-mono-data" data-testid="invoice-net-to-pay">{da(net)}</span></div>
    </div>
  );
}

function PaymentTerms({ lines, terms, setTerms }: { lines: any[]; terms: string; setTerms: (v: string) => void }) {
  const ttc = lines.reduce((s, l) => s + l.qty * l.unit_price * (1 - (l.discount || 0) / 100) * (1 + (l.tva_rate || 0) / 100), 0);
  const { rate, stamp } = stampDuty(ttc);
  return (
    <div className="mt-4 border-t border-border pt-4" data-testid="payment-terms">
      <div className="eyebrow mb-2">Modalité de paiement (entreprise)</div>
      <div className="flex flex-wrap gap-2">
        {Object.entries(PAY_METHODS).map(([k, l]) => (
          <button key={k} onClick={() => setTerms(k)} className={`h-10 rounded-sm border px-4 text-sm transition-colors ${terms === k ? "border-primary bg-primary/10" : "border-border hover:bg-accent"}`} data-testid={`payment-terms-${k}`}>{l}</button>
        ))}
      </div>
      {!terms && <p className="mt-2 text-xs text-amber-500">À choisir avant de valider la facture.</p>}
      {terms === "especes" && <StampLines rate={rate} stamp={stamp} net={ttc + stamp} />}
    </div>
  );
}

export default function InvoiceDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { data: inv, reload } = useFetch<any>(`/invoices/${id}`);
  const [lines, setLines] = useState<any[]>([]);
  const [terms, setTerms] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [credit, setCredit] = useState(false);
  useEffect(() => {
    if (inv) { setLines(inv.lines); setTerms(inv.payment_terms || ""); }
  }, [inv]);
  if (!inv) return <Loading />;
  const draft = inv.status === "brouillon";
  const validator = can(user?.role, "billing_validate");
  const body = () => ({ lines, notes: inv.notes || "", payment_terms: terms });
  const isBon = inv.kind === "facture" && inv.client_kind !== "entreprise";
  const label = isBon ? "Bon de paiement" : "Facture";

  const save = () => act(() => api.put(`/invoices/${inv.id}`, body()), "Brouillon enregistré").then((r) => r && reload());
  const validate = async () => {
    const saved = await act(() => api.put(`/invoices/${inv.id}`, body()));
    if (saved && (await act(() => api.post(`/invoices/${inv.id}/validate`), "Document validé et archivé"))) reload();
  };
  const remove = async () => {
    if (await act(() => api.delete(`/invoices/${inv.id}`), "Brouillon supprimé")) nav("/factures");
  };

  return (
    <div>
      <PageHeader eyebrow={inv.kind === "avoir" ? `Avoir sur ${inv.origin_number}` : `${label} · OR ${inv.order_number}`} title={inv.number || `${label} — brouillon`}
        actions={<>
          <PdfButton kind="invoice" id={inv.id} label={draft ? "Aperçu PDF" : "PDF archivé"} />
          {!draft && <WhatsAppButton kind="invoice" id={inv.id} label="Envoyer par WhatsApp" onSent={reload} testId="invoice-whatsapp-btn" />}
          {draft && <><Button variant="outline" onClick={remove} data-testid="invoice-delete-btn"><Trash2 className="mr-2 h-4 w-4" />Supprimer</Button><Button variant="outline" onClick={save} data-testid="invoice-save-btn">Enregistrer</Button></>}
          {draft && validator && <Button onClick={() => setConfirm(true)} data-testid="invoice-validate-btn"><BadgeCheck className="mr-2 h-4 w-4" />Valider {isBon ? "le bon" : "la facture"}</Button>}
          {!draft && inv.kind === "facture" && validator && inv.payment_status !== "annulee" && <Button variant="outline" onClick={() => setCredit(true)} data-testid="invoice-credit-btn"><Undo2 className="mr-2 h-4 w-4" />Avoir</Button>}
        </>}>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <StatusBadge status={draft ? "brouillon" : inv.payment_status} testId="invoice-status" />
          <span className="text-sm text-muted-foreground"><span className="font-mono-data">{inv.plate}</span> · {inv.client_name}{inv.validated_at ? ` · validée le ${fmtDate(inv.validated_at)} par ${inv.validated_by}` : ""}</span>
          {!draft && <span className="flex items-center gap-1 text-xs text-muted-foreground" data-testid="invoice-locked"><Lock className="h-3 w-3" />Document figé (logo v{inv.snapshot?.logo_version}, TVA {inv.snapshot?.tva_rate} %)</span>}
        </div>
      </PageHeader>
      <div className="grid gap-6 xl:grid-cols-3">
        <div className="space-y-6 xl:col-span-2">
          <Panel title="Lignes">
            {draft ? <LinesEditor lines={lines} onChange={setLines} defaultTva={0} /> : <><LinesView lines={inv.lines} /><Totals totals={inv.totals} label={inv.stamp_duty > 0 ? "Montant" : undefined} /></>}
            {inv.kind === "facture" && inv.client_kind === "entreprise" && (draft ? <PaymentTerms lines={lines} terms={terms} setTerms={setTerms} /> : <>
              {inv.payment_terms && <p className="mt-3 text-sm" data-testid="invoice-payment-terms">Modalité de paiement : <b>{PAY_METHODS[inv.payment_terms]}</b></p>}
              {inv.stamp_duty > 0 && <StampLines rate={inv.stamp_rate} stamp={inv.stamp_duty} net={inv.net_to_pay} />}</>)}
            {!draft && <p className="mt-4 text-sm italic text-muted-foreground" data-testid="invoice-amount-words">{inv.amount_words}</p>}
            {inv.reason && <p className="mt-2 text-sm">Motif : {inv.reason}</p>}
            <WhatsAppHistory entries={inv.whatsapp} testId="invoice-whatsapp-history" />
          </Panel>
          {inv.credit_notes.length > 0 && (
            <Panel title="Avoirs liés">
              {inv.credit_notes.map((c: any) => (
                <Link key={c.id} to={`/factures/${c.id}`} className="flex justify-between border-b border-border py-2 text-sm last:border-0" data-testid={`credit-note-${c.id}`}><span className="font-mono-data">{c.number} · {c.reason}</span><span className="font-mono-data">-{da(c.totals.ttc)}</span></Link>
              ))}
            </Panel>
          )}
          {inv.origin_id && <Link to={`/factures/${inv.origin_id}`} className="text-sm text-primary underline">Voir la facture d'origine {inv.origin_number}</Link>}
          {inv.kind === "avoir" && inv.refund_due > 0 && <RefundPanel inv={inv} canPay={validator} onDone={reload} />}
        </div>
        {inv.kind === "facture" && !draft && (
          <div className="space-y-6">
            <Panel title="Règlement" testId="payment-panel">
              <div className="mb-4 grid grid-cols-2 gap-3 text-sm">
                <div><div className="eyebrow">Payé</div><div className="font-mono-data text-lg">{da(inv.paid)}</div></div>
                <div><div className="eyebrow">Reste dû</div><div className="font-mono-data text-lg font-bold" data-testid="invoice-balance">{da(inv.balance)}</div></div>
              </div>
              {inv.balance > 0.009 && validator ? <PaymentForm inv={inv} onDone={reload} /> : inv.balance <= 0.009 && <p className="text-sm text-emerald-400">{isBon ? "Bon soldé." : "Facture soldée."}</p>}
              {inv.balance > 0.009 && (
                <div className="mt-4 border-t border-border pt-4">
                  <WhatsAppButton kind="reminder" id={inv.id} label={`Relancer sur WhatsApp (${da(inv.balance)})`} onSent={reload} className="h-11 w-full" testId="invoice-reminder-btn" />
                </div>
              )}
              <WhatsAppHistory entries={inv.whatsapp_reminders} testId="invoice-reminder-history" />
            </Panel>
            <Panel title="Encaissements">
              {inv.payments.length === 0 ? <Empty text="Aucun encaissement" /> : inv.payments.map((p: any) => (
                <div key={p.id} className="flex items-center justify-between gap-2 border-b border-border py-2.5 text-sm last:border-0" data-testid={`payment-${p.id}`}>
                  <div><div className="font-mono-data">{p.number} · {da(p.amount)}{p.stamp_duty ? ` + timbre ${da(p.stamp_duty)}` : ""}</div><div className="text-xs text-muted-foreground">{PAY_METHODS[p.method]} · {fmtDate(p.at)}</div></div>
                  <PdfButton kind="receipt" id={p.id} label="Reçu" testId={`receipt-btn-${p.id}`} />
                </div>
              ))}
            </Panel>
          </div>
        )}
      </div>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="validate-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Valider définitivement ?</AlertDialogTitle>
            <AlertDialogDescription>{isBon ? "Un numéro BP définitif sera attribué (client particulier : bon de paiement, pas de facture)." : "Un numéro FAC définitif sera attribué."} Le document sera figé (logo, coordonnées, NIF/RC, taux) et ne pourra plus jamais être modifié ni supprimé : seul un avoir permettra de le corriger.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="validate-cancel-btn">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={validate} data-testid="validate-confirm-btn">Valider</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {!draft && <CreditDialog inv={inv} open={credit} onOpenChange={setCredit} onDone={(cid: string) => { setCredit(false); nav(`/factures/${cid}`); }} />}
    </div>
  );
}
